import React, { useEffect } from "react";
import { FlexGrowProps } from "styled-system";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { FormikInputText } from "../FormikInputText";
import { FormInputErrorMessage } from "../FormInputErrorMessage";
import { Flex } from "../Flex";
import { Span } from "../Span";
import { IconPopupTip } from "../IconPopupTip";
import { Icon } from "../Icon";
import { List } from "../List";
import { ListItem } from "../ListItem";
import { CloseableNotificationBanner } from "../CloseableNotificationBanner";

interface Props {
  id: string;
  icpNumber: string;
  addressLabel: string;
  unknownIcp: boolean;
  loading: boolean;
  validateField: (field: string) => void;
}

export const AddressIcpLookupField: React.FC<Props & FlexGrowProps> = ({
  id,
  icpNumber,
  addressLabel,
  unknownIcp,
  loading,
  validateField,
}) => {
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    // Sets hidden fields
    setFieldValue("propertyDetails.icpAddressLookup.label", addressLabel);
    setFieldValue(
      "propertyDetails.icpAddressLookupEnabled",
      addressLabel !== "",
    );
  }, [addressLabel, setFieldValue]);

  useEffect(() => {
    validateField("propertyDetails.icpAddressLookup.id");
  }, [icpNumber, validateField]);

  return (
    <Flex id={id} flexDirection="column">
      <Flex pl={2} pr={2}>
        <Span
          display="inline-block"
          fontSize={2}
          lineHeight={2}
          fontWeight="medium"
        >
          Installation Control Point (ICP) number
        </Span>

        <IconPopupTip icon="help" ml={["auto", "auto", "auto", 3]}>
          {
            "The ICP number has 15 characters e.g. 0000012345ABC00. This unique number can be found on your electricity power bill"
          }
        </IconPopupTip>
      </Flex>
      <Field
        id={"propertyDetails.icpAddressLookup.id"}
        name={"propertyDetails.icpAddressLookup.id"}
        placeholder="e.g. 1001234561ABC12"
        value={icpNumber}
        focusable
        component={FormikInputText}
        maxWidth={"422px"}
      />
      <ErrorMessage
        name={"propertyDetails.icpAddressLookup.id"}
        className="field-error"
        component={FormInputErrorMessage}
      />

      {loading && (
        <Span mt={3} color="neutrals.mn80" fontSize={1}>
          <Icon
            icon={"data_usage"}
            mr={1}
            borderRadius="6px"
            color="neutrals.mn80"
          />
          Checking...
        </Span>
      )}

      {addressLabel && (
        <Flex flexDirection={["column", "column", "column", "row"]} mt={3}>
          <Flex flexDirection="row">
            <Icon
              icon={"check_circle"}
              mr={2}
              borderRadius="6px"
              color="newEnergy"
              backgroundColor="white"
            />

            <Span
              id="icp-lookup-address-label"
              fontSize={2}
              lineHeight={2}
              mr={3}
            >
              {addressLabel}
            </Span>
          </Flex>
          <Flex
            flexDirection="row"
            alignSelf={["flex-end", "flex-end", "flex-end", "auto"]}
          >
            <IconPopupTip icon="help" label={"Wrong address"}>
              <List>
                <ListItem>Check the ICP number is typed correctly</ListItem>
                <ListItem>
                  Remove the ICP number and use the electricity meter fields
                  below and provide the Meter serial number
                </ListItem>
              </List>
            </IconPopupTip>
          </Flex>
        </Flex>
      )}

      {unknownIcp && (
        <Flex flexDirection="column" mt={4}>
          <CloseableNotificationBanner
            title="No ICP found"
            message={() => (
              <Span>
                Check the ICP number is typed correctly.
                <br /> Remove the ICP number and input the Meter serial number
                below.
              </Span>
            )}
            icon="warning"
            variant="error"
            visible={true}
          />
        </Flex>
      )}
    </Flex>
  );
};
